import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Shown when the user signed in with an administrator-issued temporary password. Backend blocks everything else (403). */
export default function ChangePasswordRequired() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => { setError(''); setForm({ ...form, [k]: e.target.value }); };

  const submit = async (e) => {
    e.preventDefault(); setError('');
    if (form.new_password !== form.confirm_password) return setError('Passwords do not match');
    if (form.new_password.length < 12) return setError('New password must be at least 12 characters');
    setBusy(true);
    try {
      await axios.post(`${API}/auth/change-password-required`, form, { withCredentials: true });
      await checkAuth?.();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || (Array.isArray(d) ? d.map((x) => x.msg).join(' ') : 'Could not change password')));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="change-password-required-page" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm p-6">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-[#002FA7]" /><h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Set a new password</h1></div>
        <p className="text-sm text-slate-600 mb-5">You signed in with a temporary password{user?.email ? ` for ${user.email}` : ''}. Choose a new password to continue — access to CivilX is locked until you do.</p>
        <form onSubmit={submit} noValidate className="space-y-4">
          <label className="block text-xs font-semibold text-slate-600">Temporary password
            <input data-testid="cpr-current" type={show ? 'text' : 'password'} autoComplete="current-password" required value={form.current_password} onChange={set('current_password')} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block text-xs font-semibold text-slate-600">New password (12+ characters, upper/lower/number/symbol)
            <input data-testid="cpr-new" type={show ? 'text' : 'password'} autoComplete="new-password" minLength={12} required value={form.new_password} onChange={set('new_password')} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <label className="block text-xs font-semibold text-slate-600">Confirm new password
            <input data-testid="cpr-confirm" type={show ? 'text' : 'password'} autoComplete="new-password" minLength={12} required value={form.confirm_password} onChange={set('confirm_password')} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </label>
          <button type="button" data-testid="cpr-toggle-show" onClick={() => setShow((v) => !v)} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900">{show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {show ? 'Hide' : 'Show'} passwords</button>
          {error && <p data-testid="cpr-error" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}
          <button type="submit" data-testid="cpr-submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold bg-[#002FA7] text-white rounded-md hover:bg-[#00247D] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Save new password
          </button>
        </form>
        <button type="button" data-testid="cpr-logout" onClick={() => logout?.()} className="mt-4 text-xs text-slate-500 hover:text-slate-800 underline">Sign out instead</button>
      </div>
    </div>
  );
}
